import { jsx, jsxs, Fragment } from 'react/jsx-runtime';
import { useLocalization } from '@shared-ui/localization-context';
import { Viewport, ViewSmall, ViewLarge } from '@shared-ui/viewport-context';
import * as React from 'react';
import { useEffect, useState } from 'react';
import { EGDSFullscreenDialog, EGDSDialogAnimationType } from '@egds/react-core/dialog';
import { EGDSTextInput, EGDSFauxInput } from '@egds/react-core/input';
import { EGDSLayoutFlex, EGDSLayoutFlexItem } from '@egds/react-core/layout-flex';
import { classNames, liveAnnounce, Level, isElementVisible } from '@egds/react-core/utils';
import { EGDSMenu, EGDSMenuTrigger, EGDSMenuContainer, PositionType } from '@egds/react-core/menu';
import { DatePickerModel as DatePickerModel$1, EGDSDatePickerInput, EGDSCalendar } from '@egds/react-core/date-picker';
import styles from '../styles/egds-air-flexibility-date-picker-footer-additional-text.css';
import { EGDSTertiaryButton, EGDSPrimaryButton, EGDSButtonSize } from '@egds/react-core/button';
import { EGDSIcon, EGDSIconSize } from '@egds/react-core/icons';
import { EGDSSpacing } from '@egds/react-core/spacing';
import { EGDSScrim, EGDSScrimType } from '@egds/react-core/scrim';
import { EGDSToolbar, EGDSToolbarButton } from '@egds/react-core/toolbar';

const getClassNameList$3 = classNames.bind(styles);
var EGDSDatePickerTriggeredSelector;
(function (EGDSDatePickerTriggeredSelector) {
    EGDSDatePickerTriggeredSelector["START"] = "start";
    EGDSDatePickerTriggeredSelector["END"] = "end";
    EGDSDatePickerTriggeredSelector["NONE"] = "none";
})(EGDSDatePickerTriggeredSelector || (EGDSDatePickerTriggeredSelector = {}));
var EGDSDatePickerRenderType;
(function (EGDSDatePickerRenderType) {
    EGDSDatePickerRenderType["DIALOG"] = "dialog";
    EGDSDatePickerRenderType["MENU"] = "menu";
})(EGDSDatePickerRenderType || (EGDSDatePickerRenderType = {}));
class DatePicker extends React.Component {
    static defaultProps = {
        daysBookableInAdvance: 500,
        midnightBookingOffset: 4,
        preventAutoFocus: false,
        singleMode: false,
    };
    constructor(props) {
        super(props);
        const initialValues = this.setInitialDates();
        const selection = new DatePickerModel$1(initialValues.lastValidDate, props.singleDateSelect);
        let name;
        if (props.triggeredSelector) {
            name = props.triggeredSelector;
        }
        else {
            name = props.dateFieldProps.showDatePicker ? EGDSDatePickerTriggeredSelector.START : '';
        }
        this.startDomRef = props.startDateRef ? props.startDateRef : React.createRef();
        this.endDomRef = props.endDateRef ? props.endDateRef : React.createRef();
        this.state = {
            localizedText: {
                checkInInstructions: () => props.l10n.formatText('datePicker.fromDateInstructions.a11yLabel'),
                checkOutInstructions: () => props.l10n.formatText('datePicker.toDateInstructions.a11yLabel'),
                dateFieldSelected: () => props.l10n.formatText('datePicker.selectedDateField.allyLabel'),
                doneHeader: () => props.l10n.formatText('datePicker.doneHeader'),
                clearHeader: () => props.l10n.formatText('datePicker.clearHeader'),
                selectEndDate: () => props.l10n.formatText('datePicker.dateInput.endDate.placeholder'),
                selectStartDate: () => props.l10n.formatText('datePicker.dateInput.startDate.placeholder'),
                clearedDates: () => props.l10n.formatText('datepicker.cleared.dates'),
            },
            name,
            selection,
        };
        this.getDatePickerProps = this.getDatePickerProps.bind(this);
        this.onSubmit = this.onSubmit.bind(this);
        this.onClearDates = this.onClearDates.bind(this);
        this.onDismiss = this.onDismiss.bind(this);
        this.onSelectStartMode = this.onSelectStartMode.bind(this);
        this.onSelectEndMode = this.onSelectEndMode.bind(this);
        this.openDatePicker = this.openDatePicker.bind(this);
        this.selectionClassNames = this.selectionClassNames.bind(this);
        this.setInitialDates = this.setInitialDates.bind(this);
        this.updateDatePicker = this.updateDatePicker.bind(this);
        this.updateViewport = this.updateViewport.bind(this);
    }
    UNSAFE_componentWillMount() {
        this.updateStateWithProps(this.props);
    }
    componentDidMount() {
        const { props } = this;
        if (props.onMountCB) {
            props.onMountCB(this.startDomRef, this.endDomRef);
        }
    }
    UNSAFE_componentWillReceiveProps(nextProps) {
        const { startDate, endDate, validStartDate } = this.props;
        if (nextProps.startDate !== startDate || nextProps.endDate !== endDate) {
            // PRESELECT DATES
            this.preselectedStart = nextProps.startDate;
            this.preselectedEnd = nextProps.endDate;
        }
        if (nextProps.validStartDate !== validStartDate) {
            this.setFirstValidDate(nextProps);
        }
        this.updateStateWithProps(nextProps);
    }
    componentDidUpdate(prevProps) {
        const { dateFieldProps, daysBookableInAdvance } = this.props;
        const { selection } = this.state;
        if (prevProps.daysBookableInAdvance !== daysBookableInAdvance) {
            this.lastValidDate = new Date(this.today.getFullYear(), this.today.getMonth(), this.today.getDate() + daysBookableInAdvance, this.today.getDate());
            /* This is not the proper way to set state, but given the architecture
               that the selection state property has, is not possible to use setState
               in the selection property. If we try to use setState to update the property
               we will need to use spread operator for keeping the previous values when updating,
               and doing that makes the selection lose the reference for the class DatePickerSelectionState.
               This leads to errors when trying to access to the public methods of that class.
              */
            selection.lastValidEndDate = this.lastValidDate;
        }
        if (!prevProps.dateFieldProps.showDatePicker && dateFieldProps.showDatePicker) {
            if (this.isOpenInternallyDriven) {
                // Reset flag
                this.isOpenInternallyDriven = undefined;
            }
            else {
                // Client driven show date picker
                this.openDatePicker(EGDSDatePickerTriggeredSelector.START, false);
            }
        }
    }
    onSubmit() {
        const { l10n, onSubmitCB } = this.props;
        const { name, selection } = this.state;
        const { start, end } = selection;
        this.resetLiveAnnounce();
        this.updateDatePicker();
        // PRESELECT DATES
        this.preselectedEnd = l10n.formatDate(end, {
            dateFormat: 'iso-date',
            raw: 'yyyy-MM-dd',
        });
        this.preselectedStart = l10n.formatDate(start, {
            dateFormat: 'iso-date',
            raw: 'yyyy-MM-dd',
        });
        this.focusOnTrigger(name);
        // Submits and closes the datepicker
        if (onSubmitCB) {
            onSubmitCB(start, end, this.isDialog);
        }
    }
    onClearDates() {
        const { onClearDatesCB } = this.props;
        this.clearDatePicker();
        if (onClearDatesCB) {
            onClearDatesCB();
        }
    }
    onDismiss(noUpdate) {
        const { onDismissCB } = this.props;
        const { name, selection } = this.state;
        selection.start = null;
        selection.end = null;
        this.updateDatePicker();
        this.resetLiveAnnounce();
        this.focusOnTrigger(name);
        if (onDismissCB) {
            onDismissCB(this.isDialog, noUpdate);
        }
    }
    onSelectStartMode(noUpdate) {
        const { onSelectStartModeCB } = this.props;
        const { localizedText, selection } = this.state;
        selection.setCurrentTrigger('start');
        liveAnnounce(localizedText.checkInInstructions(), Level.POLITE);
        onSelectStartModeCB && onSelectStartModeCB();
        if (!noUpdate) {
            this.updateDatePicker();
        }
    }
    onSelectEndMode(noUpdate) {
        const { onSelectEndModeCB } = this.props;
        const { localizedText, selection } = this.state;
        selection.setCurrentTrigger('end');
        liveAnnounce(localizedText.checkOutInstructions(), Level.POLITE);
        onSelectEndModeCB && onSelectEndModeCB();
        if (!noUpdate) {
            this.updateDatePicker();
        }
    }
    getDatePickerProps() {
        const { analytics, buttonText, clearButtonText, currentPrice, dateFieldProps, datePickerContent, doubleMonths, endSelectionPlaceholder, firstDayOfWeek, footerText, holidays, l10n, onDoneButtonClickCB, renderType, singleMode, startSelectionPlaceholder, submitDisabled, } = this.props;
        const { localizedText, selection } = this.state;
        const startSelection = selection.start;
        const endSelection = selection.end;
        const placeholderStartSelection = startSelectionPlaceholder || localizedText.selectStartDate();
        const placeholderEndSelection = endSelectionPlaceholder || localizedText.selectEndDate();
        const formattedStartSelection = startSelection
            ? l10n.formatDate(startSelection, { dateFormat: 'full', skeleton: 'MMMEd' })
            : placeholderStartSelection;
        const formattedEndSelection = endSelection
            ? l10n.formatDate(endSelection, { dateFormat: 'full', skeleton: 'MMMEd' })
            : placeholderEndSelection;
        const datePickerAnalytics = analytics || {};
        const { closeAnalytics, doneAnalytics, nextPageAnalytics, previousPageAnalytics } = datePickerAnalytics;
        const doneButtonText = buttonText || localizedText.doneHeader();
        const clearDatesButtonText = clearButtonText || localizedText.clearHeader();
        const startSelectedMsg = this.isSelected(true) ? localizedText.dateFieldSelected() : '';
        const endSelectedMsg = this.isSelected(false) ? localizedText.dateFieldSelected() : '';
        return {
            buttonText: doneButtonText,
            clearButtonText: clearDatesButtonText,
            closeAnalytics,
            currentPrice,
            datePickerContent,
            doneAnalytics,
            doubleMonths,
            endSelectedMsg,
            firstDayOfWeek,
            firstValidDate: this.firstValidDate,
            footerText,
            formattedEndSelection,
            formattedStartSelection,
            holidays,
            labels: {
                end: dateFieldProps.endLabel,
                start: dateFieldProps.startLabel,
            },
            lastValidDate: this.lastValidDate,
            nextPageAnalytics,
            onDoneButtonClickCB,
            onSelectEndMode: this.onSelectEndMode,
            onSelectStartMode: this.onSelectStartMode,
            previousPageAnalytics,
            renderType,
            selection,
            selectionClassNames: this.selectionClassNames,
            singleMode,
            startSelectedMsg,
            submitDisabled,
            today: this.today,
            updateDatePicker: this.updateDatePicker,
        };
    }
    setFirstValidDate(props) {
        this.firstValidDate = this.today;
        if (props.validStartDate) {
            try {
                this.firstValidDate = props.l10n.parseLocalDateString(props.validStartDate, {
                    dateFormat: 'iso',
                    raw: 'yyyy-MM-dd',
                });
            }
            catch (error) {
                console.error(error);
            }
            if (this.firstValidDate < this.today) {
                this.firstValidDate = this.today;
            }
        }
    }
    setInitialDates() {
        const { cutoffDate, daysBookableInAdvance, endDate, l10n, midnightBookingOffset, singleMode, startDate } = this.props;
        this.today = new Date();
        this.today.setHours(12);
        this.today.setMinutes(0);
        this.today.setSeconds(0);
        this.today.setMilliseconds(0);
        this.lastValidDate = new Date(this.today.getFullYear(), this.today.getMonth(), this.today.getDate() + daysBookableInAdvance);
        if (cutoffDate) {
            try {
                this.lastValidDate = l10n.parseLocalDateString(cutoffDate, {
                    dateFormat: 'iso',
                    raw: 'yyyy-MM-dd',
                });
            }
            catch (error) {
                console.error(error);
            }
        }
        this.setFirstValidDate(this.props);
        this.tomorrow = new Date();
        this.tomorrow.setDate(this.firstValidDate.getDate() + 1);
        // Enabled previous date for midnight to 4am (default) bookings
        // This is to allow customers to book late night so they can check-in immediately.
        if (midnightBookingOffset) {
            const now = new Date();
            if (now.getHours() < midnightBookingOffset) {
                this.today.setDate(this.today.getDate() - 1);
            }
        }
        if (endDate && !singleMode) {
            this.preselectedEnd = endDate;
        }
        if (startDate) {
            this.preselectedStart = startDate;
        }
        return {
            firstValidDate: this.firstValidDate,
            lastValidDate: this.lastValidDate,
        };
    }
    today;
    tomorrow;
    preselectedEnd;
    preselectedStart;
    startDomRef;
    endDomRef;
    lastValidDate;
    firstValidDate;
    isDialog;
    isOpenInternallyDriven;
    updateViewport(isDialog) {
        this.isDialog = isDialog;
    }
    updateStateWithProps(props) {
        const { l10n, singleMode, triggeredSelector } = props;
        const { selection } = this.state;
        if (this.preselectedStart) {
            let start = this.firstValidDate;
            try {
                start = l10n.parseLocalDateString(this.preselectedStart, {
                    dateFormat: 'iso',
                    raw: 'yyyy-MM-dd',
                });
            }
            catch (error) {
                console.error(error);
            }
            start.setHours(12);
            if (start >= this.firstValidDate) {
                selection.start = start;
            }
            else {
                this.clearDatePicker();
                this.preselectedStart = null;
                this.preselectedEnd = null;
            }
        }
        if (this.preselectedEnd) {
            let end = this.tomorrow;
            try {
                end = l10n.parseLocalDateString(this.preselectedEnd, {
                    dateFormat: 'iso',
                    raw: 'yyyy-MM-dd',
                });
            }
            catch (error) {
                console.error(error);
            }
            end.setHours(12);
            selection.end = end;
        }
        if (!this.preselectedStart && !this.preselectedEnd) {
            selection.start = null;
            selection.end = null;
        }
        // Maybe breaking
        singleMode && delete selection.end;
        if (triggeredSelector) {
            selection.setCurrentTrigger(triggeredSelector);
        }
    }
    resetLiveAnnounce() {
        liveAnnounce('', Level.POLITE);
    }
    openDatePicker(name, isOpenInternallyDriven = true) {
        const { props } = this;
        this.isOpenInternallyDriven = isOpenInternallyDriven;
        if (name === 'start') {
            this.onSelectStartMode(true);
        }
        else {
            this.onSelectEndMode(true);
        }
        this.setState({ name });
        if (props.onOpenCB) {
            props.onOpenCB(name, this.isDialog);
        }
    }
    isSelected(isStart) {
        const { selection } = this.state;
        return ((isStart && selection.isSelectingStart) ||
            (!isStart && selection.isSelectingEnd) ||
            (isStart && !selection.isSelectingStart && !selection.isSelectingEnd));
    }
    selectionClassNames(isStart) {
        return getClassNameList$3('uitk-date-picker-top-bar-selection', { selected: this.isSelected(isStart) });
    }
    createCustomEvent(value, onChangeEvent) {
        const event = new CustomEvent('change');
        const input = document.createElement('input');
        input.value = value;
        input.addEventListener('change', (inputEvent) => {
            onChangeEvent(inputEvent);
        });
        input.dispatchEvent(event);
    }
    updateDatePicker() {
        const { localizedText, selection } = this.state;
        const { onStartDateResultSelection, onEndDateResultSelection } = this.props;
        if (onStartDateResultSelection) {
            this.createCustomEvent(selection.start, onStartDateResultSelection);
        }
        if (onEndDateResultSelection) {
            this.createCustomEvent(selection.end, onEndDateResultSelection);
        }
        this.setState({
            localizedText,
            selection,
        });
    }
    clearDatePicker() {
        const { localizedText, selection } = this.state;
        selection.start = null;
        selection.end = null;
        liveAnnounce(localizedText.clearedDates());
        this.updateDatePicker();
    }
    focusOnTrigger(name) {
        const focusRef = name === 'start' ? this.startDomRef : this.endDomRef;
        // @ts-ignore
        if (isElementVisible(focusRef.current)) {
            // @ts-ignore
            focusRef?.current?.focus();
        }
    }
    render() {
        // Const { props } = this;
        const { dialogWrapper, dateFieldProps, doubleMonths, onChangeEndDateCB, onChangeMonthCB, onChangeStartDateCB, onScrollHandler, preventAutoFocus, singleMode, triggeredSelector, useClearDates, useSingleTrigger, } = this.props;
        const { name, selection } = this.state;
        const { dateFieldWrapperClasses, dateFormat, endAnalytics, endId, endInvalid, endLabel, endName, endPlaceholder, mediumDateFormatProp, onChange, useCustomTrigger, routerLinkWrapper, showDatePicker, space, stacked, startAnalytics, startId, startInvalid, startLabel, startName, startPlaceholder, } = dateFieldProps;
        let endProps;
        let _dialogWrapper;
        if (dialogWrapper) {
            _dialogWrapper = dialogWrapper;
        }
        else {
            _dialogWrapper = (children) => (jsx("div", { id: "date-picker", className: getClassNameList$3('uitk-dialog-layer', 'uitk-dialog-layer-responsive'), children: children }));
        }
        const propsForDateField = {
            dateFieldWrapperClasses,
            dateFormat,
            dialogWrapper: _dialogWrapper,
            mediumDateFormatProp,
            onChange,
            onChangeEndDate: onChangeEndDateCB,
            onChangeMonth: onChangeMonthCB,
            onChangeStartDate: onChangeStartDateCB,
            onClearDates: this.onClearDates,
            onDismiss: this.onDismiss,
            onScrollHandler,
            onSubmit: this.onSubmit,
            openDatePicker: this.openDatePicker,
            preventAutoFocus,
            routerLinkWrapper,
            showDatePicker,
            singleMode,
            space,
            stacked,
            triggeredField: triggeredSelector || name,
            useCustomTrigger,
            useClearDates,
            useSingleTrigger,
        };
        const datePickerProps = this.getDatePickerProps();
        const startProps = {
            analytics: startAnalytics,
            dateType: EGDSDatePickerTriggeredSelector.START,
            domRef: this.startDomRef,
            doubleMonths,
            id: startId,
            invalid: startInvalid,
            label: startLabel,
            name: startName || 'startDate',
            placeholder: startPlaceholder,
            value: selection.start,
        };
        const allProps = {
            datePickerProps,
            endProps,
            startProps,
            updateViewport: this.updateViewport,
            ...propsForDateField,
        };
        if (!singleMode) {
            endProps = {
                analytics: endAnalytics,
                dateType: EGDSDatePickerTriggeredSelector.END,
                domRef: this.endDomRef,
                doubleMonths,
                id: endId,
                invalid: endInvalid,
                label: endLabel,
                name: endName || 'endDate',
                placeholder: endPlaceholder,
                value: selection.end,
            };
            // @ts-ignore
            allProps.endProps = endProps;
        }
        return jsx(EGDSDateField, { ...allProps });
    }
}
const DatePickerFooterAdditionalText = (props) => {
    const l10n = useLocalization();
    return jsx(DatePicker, { l10n: l10n, ...props });
};
DatePickerFooterAdditionalText.displayName = 'DatePickerFooterAdditionalText';

const getClassNameList$2 = classNames.bind(styles);
class DatePickerMenuView extends React.Component {
    static displayName = 'DatePickerFooterAdditionalTextMenuView';
    elementRef;
    constructor(props) {
        super(props);
        this.elementRef = React.createRef();
        this.state = {
            localizedText: {
                cancelButton: () => props.l10n.formatText('datePicker.cancelButton.a11yLabel'),
                datePickerOpened: () => props.l10n.formatText('datePicker.datePickerOpened.liveAnnounce'),
                doneButtonFare: (nights, lowestFareMessage, startDate, endDate) => {
                    if (nights <= 1) {
                        return props.l10n.formatText('datePicker.doneButtonFareWithSummary.single.a11yLabel', startDate, lowestFareMessage);
                    }
                    return props.l10n.formatText('datePicker.doneButtonFareWithSummary.multiple.a11yLabel', startDate, endDate, lowestFareMessage);
                },
                clearInstructions: () => props.l10n.formatText('datePicker.clearInstructions.a11yLabel'),
                doneInstructions: () => props.l10n.formatText('datePicker.doneInstructions.a11yLabel'),
                hiddenDoneButton: (nights, startDate, endDate) => {
                    if (nights <= 1) {
                        return props.l10n.formatText('datePicker.doneButtonWithSummary.single.a11yLabel', startDate);
                    }
                    return props.l10n.formatText('datePicker.doneButtonWithSummary.multiple.a11yLabel', startDate, endDate);
                },
            },
        };
        this.onClickTopStart = this.onClickTopStart.bind(this);
        this.onClickTopEnd = this.onClickTopEnd.bind(this);
        this.onSubmitDates = this.onSubmitDates.bind(this);
        this.onChangeMonth = this.onChangeMonth.bind(this);
        this.onChangeStartDate = this.onChangeStartDate.bind(this);
        this.onChangeEndDate = this.onChangeEndDate.bind(this);
        this.focusOnElement = this.focusOnElement.bind(this);
        this.hiddenDoneButton = this.hiddenDoneButton.bind(this);
    }
    componentDidMount() {
        const { formattedStartSelection, labels, preventAutoFocus, startSelectedMsg } = this.props;
        const { localizedText } = this.state;
        if (this.elementRef && this.elementRef.current) {
            // @ts-ignore
            const startInputRef = this.elementRef.current.getElementsByTagName('button')[0];
            if (!preventAutoFocus) {
                this.focusOnElement(startInputRef);
            }
        }
        const datePickerOpenedString = localizedText.datePickerOpened();
        liveAnnounce(`${datePickerOpenedString}, 
      ${formattedStartSelection} ${labels.start} ${startSelectedMsg}`);
    }
    // @ts-ignore
    shouldComponentUpdate(nextProps) {
        const { selection } = this.props;
        if (nextProps && nextProps.holidays && nextProps.holidays.length > 0) {
            selection.configureHolidays(nextProps.holidays);
        }
        return true;
    }
    onClickTopStart() {
        const { onSelectStartMode } = this.props;
        onSelectStartMode();
    }
    onClickTopEnd() {
        const { onSelectEndMode } = this.props;
        onSelectEndMode();
    }
    onSubmitDates() {
        const { onSubmit, onDoneButtonClickCB } = this.props;
        if (onDoneButtonClickCB) {
            const { selection } = this.props;
            const { start, end } = selection;
            onDoneButtonClickCB(start, end);
        }
        else {
            onSubmit();
        }
    }
    onChangeMonth(month) {
        const { onChangeMonth } = this.props;
        if (onChangeMonth) {
            onChangeMonth(month, false);
        }
    }
    onChangeStartDate(date) {
        const { onChangeStartDate } = this.props;
        if (onChangeStartDate) {
            onChangeStartDate(date, false);
        }
    }
    onChangeEndDate(date) {
        const { onChangeEndDate } = this.props;
        if (onChangeEndDate) {
            onChangeEndDate(date, false);
        }
    }
    focusOnElement(ref) {
        if (isElementVisible(ref)) {
            ref.focus();
        }
    }
    hiddenDoneButton() {
        const { formattedStartSelection, formattedEndSelection, selection } = this.props;
        const { localizedText } = this.state;
        const nights = selection.numberOfNights;
        const visuallyHiddenText = localizedText.hiddenDoneButton(nights, formattedStartSelection, formattedEndSelection);
        return (jsx("button", { type: "button", className: getClassNameList$2('is-visually-hidden'), onClick: this.onSubmitDates, children: visuallyHiddenText }));
    }
    render() {
        const { buttonText, clearButtonText, currentPrice, datePickerContent, doneAnalytics, doubleMonths, endSelectedMsg, firstDayOfWeek, firstValidDate, footerText, formattedEndSelection, formattedStartSelection, holidays, labels, lastValidDate, nextPageAnalytics, onClearDates, previousPageAnalytics, selection, selectionClassNames, singleMode, startSelectedMsg, submitDisabled, updateDatePicker, useClearDates, } = this.props;
        const { localizedText } = this.state;
        const monthsToRender = doubleMonths ? 2 : 1;
        const isDateSelected = singleMode ? selection.start : selection.start && selection.end;
        return (
        // @ts-ignore
        jsxs("div", { className: getClassNameList$2('uitk-date-picker', 'date-picker-menu'), ref: this.elementRef, children: [jsxs(EGDSLayoutFlex, { alignItems: "center", className: getClassNameList$2('uitk-date-picker-selection-bar', 'uitk-date-picker-selection-bar-top'), tag: "section", children: [jsx(EGDSLayoutFlexItem, { children: jsx(EGDSDatePickerInput, { cssClassNames: selectionClassNames(true), isDialog: false, onClick: this.onClickTopStart, selectionDate: formattedStartSelection, selectionDateMsg: `${labels.start} ${startSelectedMsg}`, selection: selection.start, singleMode: singleMode }) }), !singleMode && (jsx(EGDSLayoutFlexItem, { className: getClassNameList$2('uitk-date-picker-selection-icon'), children: jsx(EGDSIcon, { name: "arrow_forward", size: EGDSIconSize.SMALL }) })), !singleMode && (jsx(EGDSLayoutFlexItem, { children: jsx(EGDSDatePickerInput, { cssClassNames: selectionClassNames(false), isDialog: false, onClick: this.onClickTopEnd, selectionDate: formattedEndSelection, selectionDateMsg: `${labels.end} ${endSelectedMsg}`, selection: selection.end }) })), this.hiddenDoneButton()] }), jsx(EGDSCalendar, { buttonText: buttonText, datePickerContent: datePickerContent, firstMonth: selection.start || firstValidDate, monthsToRender: monthsToRender, isDialog: false, selection: selection, selectionStart: selection.start, selectionEnd: selection.end, startDate: firstValidDate, endDate: lastValidDate, updateDatePicker: updateDatePicker, firstDayOfWeek: firstDayOfWeek, singleMode: singleMode, nextPageAnalytics: nextPageAnalytics, previousPageAnalytics: previousPageAnalytics, holidays: holidays, onChangeMonth: this.onChangeMonth, onChangeStartDate: this.onChangeStartDate, onChangeEndDate: this.onChangeEndDate }), jsxs(EGDSLayoutFlex, { className: getClassNameList$2('uitk-date-picker-menu-footer'), children: [!currentPrice &&
                            (footerText || (selection.holidaysConfigured && selection.holidaysName !== '')) && [
                            jsx(EGDSLayoutFlexItem, { className: getClassNameList$2('uitk-date-picker-footer-text'), children: jsxs("div", { children: [selection.holidaysConfigured && selection.holidaysName !== '' && (jsx("p", { children: selection.holidaysName }, "holiday")), footerText && jsx("p", { children: footerText }, "legal")] }) }, "0"),
                        ], footerText && currentPrice && (jsx(EGDSLayoutFlexItem, { children: jsxs("div", { children: [currentPrice && (jsx("p", { className: getClassNameList$2('uitk-date-picker-footer-text-current-price'), children: currentPrice })), footerText && (jsx("p", { className: getClassNameList$2('uitk-date-picker-footer-text-price-summary'), children: footerText }, "legal"))] }) }, "1")), jsx(EGDSLayoutFlexItem, { className: getClassNameList$2('dialog-done'), shrink: 0, children: jsxs("div", { children: [useClearDates && (jsx(EGDSSpacing, { margin: { inline: 'three' }, children: jsx(EGDSTertiaryButton, { "aria-label": localizedText.clearInstructions(), disabled: !isDateSelected, onClick: onClearDates, "data-stid": "clear-date-picker", children: clearButtonText }) })), jsx(EGDSPrimaryButton, { "aria-label": localizedText.doneInstructions(), analytics: doneAnalytics, onClick: this.onSubmitDates, "data-stid": "apply-date-picker", disabled: submitDisabled, children: buttonText })] }) })] })] }));
    }
}
const DatePickerFooterAdditionalTextMenuView = (props) => {
    const l10n = useLocalization();
    return jsx(DatePickerMenuView, { l10n: l10n, ...props });
};
DatePickerFooterAdditionalTextMenuView.displayName = 'DatePickerFooterAdditionalTextMenuView';
const EGDSNewDatePickerMenuView = DatePickerFooterAdditionalTextMenuView;

const getClassNameList$1 = classNames.bind(styles);
class DatePickerDialogView extends React.Component {
    static displayName = 'DatePickerFooterAdditionalTextDialogView';
    constructor(props) {
        super(props);
        this.doneButtonRef = React.createRef();
        this.state = {
            localizedText: {
                cancelButton: () => props.l10n.formatText('datePicker.cancelButton.a11yLabel'),
                datePickerOpened: () => props.l10n.formatText('datePicker.datePickerOpened.liveAnnounce'),
                doneButtonFare: (nights, lowestFareMessage, startDate, endDate) => {
                    if (nights <= 1) {
                        return props.l10n.formatText('datePicker.doneButtonFareWithSummary.single.a11yLabel', startDate, lowestFareMessage);
                    }
                    return props.l10n.formatText('datePicker.doneButtonFareWithSummary.multiple.a11yLabel', startDate, endDate, lowestFareMessage);
                },
                clearInstructions: () => props.l10n.formatText('datePicker.clearInstructions.a11yLabel'),
                doneInstructions: () => props.l10n.formatText('datePicker.doneInstructions.a11yLabel'),
                hiddenDoneButton: (nights, startDate, endDate) => {
                    if (nights <= 1) {
                        return props.l10n.formatText('datePicker.doneButtonWithSummary.single.a11yLabel', startDate);
                    }
                    return props.l10n.formatText('datePicker.doneButtonWithSummary.multiple.a11yLabel', startDate, endDate);
                },
                // The "nightSummary" function is written like this to support older versions of uitk-react-localization (pre HoB)
                // if you are graduating this component please change this function to
                // nightSummary: (nights: number) => props.l10n.formatText('datePicker.nightSummary', nights),
                // and ensure teams are on the latest version of uitk-react-localization
                nightSummary: (nights) => props.l10n.formatText('datePicker.nightSummary', nights) ||
                    (nights <= 1
                        ? props.l10n.formatText('datePicker.nightSummary.single', nights)
                        : props.l10n.formatText('datePicker.nightSummary.multiple', nights)),
                skipToButton: (buttonName) => props.l10n.formatText('datePicker.skipToButton', buttonName),
            },
            selection: props.selection,
        };
        this.onClickTopStart = this.onClickTopStart.bind(this);
        this.onClickTopEnd = this.onClickTopEnd.bind(this);
        this.onSubmitClick = this.onSubmitClick.bind(this);
        this.onDismissClick = this.onDismissClick.bind(this);
        this.onSelectDate = this.onSelectDate.bind(this);
        this.internallyUpdateDatePicker = this.internallyUpdateDatePicker.bind(this);
        this.onChangeStartDate = this.onChangeStartDate.bind(this);
        this.onChangeEndDate = this.onChangeEndDate.bind(this);
        this.hiddenDoneButton = this.hiddenDoneButton.bind(this);
        this.skipToDoneButton = this.skipToDoneButton.bind(this);
    }
    componentDidMount() {
        const { localizedText } = this.state;
        liveAnnounce(localizedText.datePickerOpened(), Level.ASSERTIVE);
    }
    shouldComponentUpdate(nextProps) {
        if (nextProps && nextProps.holidays && nextProps.holidays.length > 0) {
            const { selection } = this.props;
            selection.configureHolidays(nextProps.holidays);
        }
        return true;
    }
    onClickTopStart() {
        const { onSelectStartMode } = this.props;
        onSelectStartMode();
        this.internallyUpdateDatePicker();
    }
    onClickTopEnd() {
        const { onSelectEndMode } = this.props;
        onSelectEndMode();
        this.internallyUpdateDatePicker();
    }
    onSubmitClick() {
        const { onSubmit, onDoneButtonClickCB } = this.props;
        if (onDoneButtonClickCB) {
            const { selection } = this.props;
            const { start, end } = selection;
            onDoneButtonClickCB(start, end);
        }
        else {
            onSubmit();
        }
    }
    onDismissClick() {
        const { onDismiss } = this.props;
        onDismiss();
    }
    onChangeStartDate(date) {
        const { onChangeStartDate } = this.props;
        if (onChangeStartDate) {
            onChangeStartDate(date, true);
        }
    }
    onChangeEndDate(date) {
        const { onChangeEndDate } = this.props;
        if (onChangeEndDate) {
            onChangeEndDate(date, false);
        }
    }
    /* Creating another state */
    onSelectDate() {
        this.internallyUpdateDatePicker();
    }
    doneButtonRef;
    buttonText;
    // Workaround to having FullscreenDialog in the middle of the tree
    internallyUpdateDatePicker() {
        // eslint-disable-next-line react/no-access-state-in-setstate
        this.setState(this.state);
    }
    /* ********************** */
    skipToDoneButton() {
        const { localizedText } = this.state;
        const onClick = () => {
            if (this.doneButtonRef && this.doneButtonRef.current) {
                // @ts-ignore
                this.doneButtonRef.current.focus();
            }
        };
        return (jsx("button", { type: "button", className: getClassNameList$1('is-visually-hidden', 'uitk-date-picker-skip-to-done'), onClick: onClick, children: localizedText.skipToButton(this.buttonText) }));
    }
    hiddenDoneButton() {
        const { formattedStartSelection, formattedEndSelection, selection } = this.props;
        const { localizedText } = this.state;
        const nights = selection.numberOfNights;
        const startDate = formattedStartSelection;
        const endDate = formattedEndSelection;
        const visuallyHiddenText = localizedText.hiddenDoneButton(nights, startDate, endDate);
        return (jsx("button", { type: "button", className: getClassNameList$1('is-visually-hidden'), onClick: this.onSubmitClick, children: visuallyHiddenText }));
    }
    uid = Math.random().toString(36).substring(7);
    renderApplyButton(doneAnalytics, buttonText) {
        const { localizedText } = this.state;
        const { submitDisabled } = this.props;
        return (jsx(EGDSPrimaryButton, { analytics: doneAnalytics, "data-stid": "apply-date-picker", disabled: submitDisabled, 
            // @ts-ignore
            domRef: this.doneButtonRef, onClick: this.onSubmitClick, size: EGDSButtonSize.LARGE, "aria-label": localizedText.doneInstructions(), className: getClassNameList$1('dialog-done-additional'), children: buttonText }));
    }
    render() {
        const { buttonText, clearButtonText, currentPrice, footerText, closeAnalytics, datePickerContent, doneAnalytics, endSelectedMsg, firstDayOfWeek, formattedStartSelection, lastValidDate, onScrollHandler, selectionClassNames, startSelectedMsg, singleMode, updateDatePicker, labels, l10n, holidays, firstValidDate, useClearDates, onClearDates, } = this.props;
        this.buttonText = buttonText;
        const { selection } = this.state;
        const startSelection = selection.start;
        const endSelection = selection.end;
        const isDateSelected = singleMode ? startSelection : startSelection && endSelection;
        const startSelectionFormatted = startSelection
            ? l10n.formatDate(startSelection, { dateFormat: 'full', skeleton: 'MMMEd' })
            : formattedStartSelection;
        const formattedEndSelection = endSelection
            ? l10n.formatDate(endSelection, { dateFormat: 'full', skeleton: 'MMMEd' })
            : l10n.formatText('datePicker.dateInput.endDate.placeholder');
        const { localizedText } = this.state;
        const showHolidaysInfo = !!(selection.holidaysConfigured && selection.holidaysName !== '');
        const isScrim = !!(!footerText && !selection.holidaysConfigured);
        const scrimPaddingClassname = getClassNameList$1({
            'uitk-calendar-scrim': isScrim,
        });
        const clearDatesButtonId = `clear-date-picker-${this.uid}`;
        if (!selection.holidaysName) {
            selection.holidaysName = [];
        }
        return (jsxs(EGDSLayoutFlex, { direction: "column", className: getClassNameList$1('uitk-date-picker', 'date-picker-dialog'), children: [jsx(EGDSLayoutFlexItem, { children: jsx(EGDSToolbar, { className: getClassNameList$1('uitk-date-picker-toolbar'), navigationContent: {
                            navIconLabel: localizedText.cancelButton(),
                            onClick: this.onDismissClick,
                            onClickAnalytics: closeAnalytics,
                        }, ...(useClearDates && {
                            toolbarActionContent: (jsx(EGDSToolbarButton, { "aria-label": localizedText.clearInstructions(), buttonId: clearDatesButtonId, disabled: !isDateSelected, onClick: onClearDates, children: clearButtonText })),
                        }), children: jsxs(EGDSLayoutFlex, { alignItems: "center", className: getClassNameList$1('uitk-date-picker-selection-bar', 'uitk-date-picker-selection-bar-top'), tag: "section", children: [jsx(EGDSLayoutFlexItem, { children: jsx(EGDSDatePickerInput, { cssClassNames: selectionClassNames(true), isDialog: true, onClick: this.onClickTopStart, selectionDate: startSelectionFormatted, selectionDateMsg: `${labels.start} ${startSelectedMsg}`, selection: selection.start, singleMode: singleMode }) }), !singleMode && (jsxs(Fragment, { children: [jsx(EGDSLayoutFlexItem, { className: getClassNameList$1('uitk-date-picker-selection-icon'), children: jsx(EGDSIcon, { name: "arrow_forward", size: EGDSIconSize.SMALL }) }), jsx(EGDSLayoutFlexItem, { children: jsx(EGDSDatePickerInput, { cssClassNames: selectionClassNames(false), isDialog: true, onClick: this.onClickTopEnd, selectionDate: formattedEndSelection, selectionDateMsg: `${labels.end} ${endSelectedMsg}`, selection: selection.end }) })] })), this.hiddenDoneButton()] }) }) }), jsx(EGDSLayoutFlexItem, { children: jsx(EGDSCalendar, { buttonText: buttonText, className: scrimPaddingClassname, firstMonth: firstValidDate, skipToDoneButton: this.skipToDoneButton, datePickerContent: datePickerContent, isDialog: true, selection: selection, selectionStart: selection.start, selectionEnd: selection.end, startDate: firstValidDate, endDate: lastValidDate, updateDatePicker: updateDatePicker, firstDayOfWeek: firstDayOfWeek, onSelectDate: this.onSelectDate, singleMode: singleMode, holidays: holidays, onChangeStartDate: this.onChangeStartDate, onChangeEndDate: this.onChangeEndDate, onScrollHandler: onScrollHandler }) }), jsxs(EGDSLayoutFlexItem, { children: [isScrim && (jsx(EGDSScrim, { type: EGDSScrimType.BUTTON, className: getClassNameList$1('uitk-date-picker-footer'), children: this.renderApplyButton(doneAnalytics, buttonText) })), !isScrim && (jsxs("div", { className: getClassNameList$1('uitk-date-picker-footer-opaque-additional'), children: [showHolidaysInfo && (jsx("div", { className: getClassNameList$1('uitk-date-picker-footer-text'), children: jsx("p", { children: selection.holidaysName }) })), !currentPrice && footerText && (jsx("p", { className: getClassNameList$1('uitk-date-picker-footer-text', {
                                        'uitk-date-picker-footer-text-center': !showHolidaysInfo,
                                    }), children: footerText })), footerText && currentPrice && (jsx(EGDSLayoutFlexItem, { className: getClassNameList$1('uitk-date-picker-footer-summary'), children: jsxs("div", { children: [currentPrice && (jsx("p", { className: getClassNameList$1('uitk-date-picker-footer-text-current-price'), children: currentPrice })), footerText && (jsx("p", { className: getClassNameList$1('uitk-date-picker-footer-text-price-summary'), children: footerText }, "legal"))] }) }, "1")), this.renderApplyButton(doneAnalytics, buttonText)] }))] })] }));
    }
}
const DatePickerFooterAdditionalTextDialogView = (props) => {
    const l10n = useLocalization();
    return jsx(DatePickerDialogView, { l10n: l10n, ...props });
};
DatePickerFooterAdditionalTextDialogView.displayName = 'DatePickerFooterAdditionalTextDialogView';
const EGDSNewDatePickerDialogView = DatePickerFooterAdditionalTextDialogView;

const getClassNameList = classNames.bind(styles);
var EGDSDatePickerDateFormat;
(function (EGDSDatePickerDateFormat) {
    EGDSDatePickerDateFormat["YMD"] = "yyyy-MM-dd";
})(EGDSDatePickerDateFormat || (EGDSDatePickerDateFormat = {}));
const mapDatePickerProps = (source) => ({
    ...source.datePickerProps,
    onChangeEndDate: source.onChangeEndDate,
    onChangeMonth: source.onChangeMonth,
    onChangeStartDate: source.onChangeStartDate,
    onClearDates: source.onClearDates,
    onDismiss: source.onDismiss,
    onScrollHandler: source.onScrollHandler,
    onSubmit: source.onSubmit,
    preventAutoFocus: source.preventAutoFocus,
    singleMode: source.singleMode && !source.useSingleTrigger,
    useClearDates: source.useClearDates,
    useSingleTrigger: source.useSingleTrigger,
});
/**
 * Get display values and values for date field
 */
const getFormattedDates = (l10n, dateProps, dateFormat, mediumDateFormatProp) => {
    const mediumDateFormat = { dateFormat: 'medium', skeleton: mediumDateFormatProp ?? 'MMMd' };
    const isoDateFormat = { dateFormat: 'iso-date', raw: dateFormat };
    const { value, placeholder } = dateProps;
    return {
        value: l10n.formatDate(value, isoDateFormat),
        formattedBtnText: value ? l10n.formatDate(value, mediumDateFormat) : placeholder,
        accessibleFormattedText: value ? l10n.formatDate(value, mediumDateFormat) : placeholder,
        label: jsx("span", { "aria-hidden": "true", children: dateProps.label }),
    };
};
const CustomTriggerDialog = (props) => {
    const { updateViewport } = props;
    useEffect(() => {
        if (updateViewport) {
            updateViewport(true);
        }
    }, []);
    const enhancedDatePickerProps = mapDatePickerProps(props);
    return (jsx(EGDSFullscreenDialog, { dialogShow: true, animation: EGDSDialogAnimationType.SLIDEUP, children: jsx(DatePickerFooterAdditionalTextDialogView, { ...enhancedDatePickerProps }) }));
};
const CustomTriggerMenu = (props) => {
    const { updateViewport } = props;
    useEffect(() => {
        if (updateViewport) {
            updateViewport(false);
        }
    }, []);
    const enhancedDatePickerProps = mapDatePickerProps(props);
    return jsx(DatePickerFooterAdditionalTextMenuView, { ...enhancedDatePickerProps });
};
CustomTriggerDialog.displayName = 'CustomTriggerDialog';
const DialogEndDateField = (props) => {
    const { dateFormat, endProps, l10n, mediumDateFormatProp, onChange, routerLinkWrapper, openDatePicker } = props;
    const [isMounted, setIsMounted] = useState(false);
    useEffect(() => {
        setIsMounted(true);
    }, []);
    if (!endProps) {
        return null;
    }
    const { value, accessibleFormattedText, formattedBtnText, label } = getFormattedDates(l10n, endProps, dateFormat, mediumDateFormatProp);
    const buttonLabel = `${endProps.label} ${accessibleFormattedText}`;
    const setContext = () => {
        openDatePicker(endProps.dateType);
    };
    const textInputClasses = getClassNameList({
        'is-hidden': isMounted,
    });
    return (jsx(EGDSTextInput, { id: endProps.id, name: endProps.name, className: textInputClasses, label: label, placeholder: endProps.placeholder, value: value, onChange: onChange, "data-stid": "input-date", icon: "today", invalid: endProps.invalid, children: isMounted &&
            routerLinkWrapper &&
            routerLinkWrapper(jsx(EGDSFauxInput, { "aria-label": buttonLabel, className: getClassNameList('uitk-form-field-trigger'), domRef: endProps.domRef, "data-name": endProps.name, id: `${endProps.id}-btn`, "data-stid": "open-date-picker", type: "button", onClick: setContext, analytics: endProps.analytics, children: formattedBtnText })) }));
};
DialogEndDateField.displayName = 'DialogEndDateField';
const DialogDateFields = (props) => {
    const { updateViewport } = props;
    const [isMounted, setIsMounted] = useState(false);
    useEffect(() => {
        setIsMounted(true);
        if (updateViewport) {
            updateViewport(true);
        }
    }, []);
    const { dateFieldWrapperClasses, dateFormat, dialogWrapper, l10n, mediumDateFormatProp, onChange, routerLinkWrapper, showDatePicker, singleMode, space, stacked, startProps, endProps, triggeredField, useSingleTrigger, openDatePicker, } = props;
    const { value: startValue, formattedBtnText: startFormattedBtnText, accessibleFormattedText: startAccessibleFormattedText, label: startLabel, } = getFormattedDates(l10n, startProps, dateFormat, mediumDateFormatProp);
    let value = startValue;
    let formattedBtnText = startFormattedBtnText;
    let buttonLabel = `${startProps.label}`;
    if (startAccessibleFormattedText) {
        buttonLabel += ` ${startAccessibleFormattedText}`;
    }
    if (useSingleTrigger && endProps) {
        const { value: endValue, formattedBtnText: endFormattedBtnText, accessibleFormattedText: endAccessibleFormattedText, } = getFormattedDates(l10n, endProps, dateFormat, mediumDateFormatProp);
        if (endValue) {
            value += ` - ${endValue}`;
        }
        if (endFormattedBtnText) {
            formattedBtnText += ` - ${endFormattedBtnText}`;
        }
        if (endAccessibleFormattedText) {
            buttonLabel = `${startProps.label} ${l10n.formatText('datepicker.buttonLabel.endAccessibleFormattedText', startAccessibleFormattedText, endAccessibleFormattedText)}`;
        }
    }
    const setContext = () => {
        openDatePicker(startProps.dateType);
    };
    const shouldBeStacked = stacked && !singleMode && !useSingleTrigger;
    const fieldWrapperClasses = getClassNameList({
        'uitk-date-field-wrapper': !shouldBeStacked,
    });
    const fieldLayoutDirection = shouldBeStacked ? 'column' : 'row';
    const wrapperClasses = getClassNameList('egds-team-date-picker-with-footer-additional-text', {
        [`${dateFieldWrapperClasses}`]: dateFieldWrapperClasses,
    }, 'uitk-date-fields-wrapper');
    const enhancedDatePickerProps = mapDatePickerProps(props);
    const textInputClasses = getClassNameList({
        'is-hidden': isMounted,
    });
    return (jsx(EGDSLayoutFlexItem, { className: wrapperClasses, grow: 1, children: jsxs(EGDSLayoutFlex, { className: getClassNameList('uitk-date-fields'), direction: fieldLayoutDirection, space: space, children: [showDatePicker &&
                    triggeredField &&
                    dialogWrapper(jsx(EGDSFullscreenDialog, { dialogShow: true, animation: EGDSDialogAnimationType.SLIDEUP, children: jsx(DatePickerFooterAdditionalTextDialogView, { ...enhancedDatePickerProps }) }), triggeredField), jsx(EGDSLayoutFlexItem, { className: fieldWrapperClasses, grow: 1, minWidth: "zero", maxWidth: singleMode || useSingleTrigger || stacked ? undefined : 'half_width', children: jsx("div", { children: jsx(EGDSTextInput, { id: startProps.id, name: startProps.name, className: textInputClasses, label: startLabel, placeholder: startProps.placeholder, value: value, onChange: onChange, "data-stid": "input-date", icon: "today", invalid: startProps.invalid, children: isMounted &&
                                routerLinkWrapper &&
                                routerLinkWrapper(jsx(EGDSFauxInput, { "aria-label": buttonLabel, className: getClassNameList('uitk-form-field-trigger'), domRef: startProps.domRef, "data-name": startProps.name, id: `${startProps.id}-btn`, "data-stid": "open-date-picker", type: "button", onClick: setContext, analytics: startProps.analytics, children: formattedBtnText })) }) }) }), !singleMode && !useSingleTrigger && (jsx(EGDSLayoutFlexItem, { className: fieldWrapperClasses, grow: 1, minWidth: "zero", maxWidth: stacked ? undefined : 'half_width', children: jsx("div", { children: jsx(DialogEndDateField, { ...props }) }) }))] }) }));
};
DialogDateFields.displayName = 'DialogDateFields';
const MenuEndDateField = (props) => {
    const { dateFormat, mediumDateFormatProp, endProps, l10n, onChange, onSubmit, showDatePicker, triggeredField, openDatePicker, } = props;
    const [isMounted, setIsMounted] = useState(false);
    useEffect(() => {
        setIsMounted(true);
    }, []);
    if (!endProps) {
        return null;
    }
    const { value, formattedBtnText, accessibleFormattedText, label } = getFormattedDates(l10n, endProps, dateFormat, mediumDateFormatProp);
    const buttonLabel = `${endProps.label} ${accessibleFormattedText}`;
    const setContext = () => {
        openDatePicker(endProps.dateType);
    };
    // Make sure that both menus aren't opened at the same time on auto-open
    const isOpen = isMounted && showDatePicker;
    const containerClasses = getClassNameList('uitk-date-picker-menu-container', {
        'uitk-date-picker-menu-container-single': !endProps.doubleMonths,
        'uitk-date-picker-menu-container-double': endProps.doubleMonths,
    });
    const enhancedDatePickerProps = mapDatePickerProps(props);
    const textInputClasses = getClassNameList({
        'is-hidden': isMounted,
    });
    return (jsxs(EGDSMenu, { isOpen: isOpen, className: getClassNameList('uitk-date-picker-menu'), onDismiss: onSubmit, children: [jsx(EGDSMenuTrigger, { children: jsx(EGDSTextInput, { className: textInputClasses, id: endProps.id, name: endProps.name, label: label, placeholder: endProps.placeholder, value: value, onChange: onChange, "data-stid": "input-date", icon: "today", invalid: endProps.invalid, children: isMounted && (jsx(EGDSFauxInput, { "aria-label": buttonLabel, className: getClassNameList('uitk-form-field-trigger'), domRef: endProps.domRef, "data-name": endProps.name, id: `${endProps.id}-btn`, "data-stid": "open-date-picker", type: "button", onClick: setContext, analytics: endProps.analytics, children: formattedBtnText })) }) }), showDatePicker && triggeredField === 'end' && (jsx(EGDSMenuContainer, { position: PositionType.LEFT, className: containerClasses, autoPosition: true, children: jsx(DatePickerFooterAdditionalTextMenuView, { ...enhancedDatePickerProps }) }))] }));
};
MenuEndDateField.displayName = 'MenuEndDateField';
const MenuDateFields = (props) => {
    const { updateViewport, openDatePicker } = props;
    const [isMounted, setIsMounted] = useState(false);
    useEffect(() => {
        setIsMounted(true);
        if (updateViewport) {
            updateViewport(false);
        }
    }, []);
    const { dateFieldWrapperClasses, dateFormat, l10n, mediumDateFormatProp, onChange, onSubmit, showDatePicker, singleMode, space, stacked, startProps, endProps, triggeredField, useSingleTrigger, } = props;
    const { value: startValue, formattedBtnText: startFormattedBtnText, accessibleFormattedText: startAccessibleFormattedText, label: startLabel, } = getFormattedDates(l10n, startProps, dateFormat, mediumDateFormatProp);
    let value = startValue;
    let formattedBtnText = startFormattedBtnText;
    let buttonLabel = `${startProps.label}`;
    if (startAccessibleFormattedText) {
        buttonLabel += ` ${startAccessibleFormattedText}`;
    }
    if (useSingleTrigger && endProps) {
        const { value: endValue, formattedBtnText: endFormattedBtnText, accessibleFormattedText: endAccessibleFormattedText, } = getFormattedDates(l10n, endProps, dateFormat, mediumDateFormatProp);
        if (endValue) {
            value += ` - ${endValue}`;
        }
        if (endFormattedBtnText) {
            formattedBtnText += ` - ${endFormattedBtnText}`;
        }
        if (endAccessibleFormattedText) {
            buttonLabel = `${startProps.label} ${l10n.formatText('datepicker.buttonLabel.endAccessibleFormattedText', startAccessibleFormattedText, endAccessibleFormattedText)}`;
        }
    }
    const setContext = () => {
        openDatePicker(startProps.dateType);
    };
    const isOpen = showDatePicker;
    const containerClasses = getClassNameList('uitk-date-picker-menu-container', {
        'uitk-date-picker-menu-container-single': !startProps.doubleMonths,
        'uitk-date-picker-menu-container-double': startProps.doubleMonths,
    });
    const enhancedDatePickerProps = mapDatePickerProps(props);
    const shouldBeStacked = stacked && !singleMode && !useSingleTrigger;
    const fieldWrapperClasses = getClassNameList({
        'uitk-date-field-wrapper': !shouldBeStacked,
    });
    const fieldLayoutDirection = shouldBeStacked ? 'column' : 'row';
    const wrapperClasses = getClassNameList('egds-team-date-picker-with-footer-additional-text', {
        [`${dateFieldWrapperClasses}`]: dateFieldWrapperClasses,
    }, 'uitk-date-fields-wrapper');
    const textInputClasses = getClassNameList({
        'is-hidden': isMounted,
    });
    return (jsx(EGDSLayoutFlexItem, { className: wrapperClasses, grow: 1, children: jsxs(EGDSLayoutFlex, { className: getClassNameList('uitk-date-fields'), space: space, direction: fieldLayoutDirection, children: [jsx(EGDSLayoutFlexItem, { className: fieldWrapperClasses, grow: 1, minWidth: "zero", maxWidth: singleMode || useSingleTrigger || stacked ? undefined : 'half_width', children: jsx("div", { children: jsxs(EGDSMenu, { isOpen: isOpen, className: getClassNameList('uitk-date-picker-menu'), onDismiss: onSubmit, children: [jsx(EGDSMenuTrigger, { children: jsx(EGDSTextInput, { className: textInputClasses, id: startProps.id, name: startProps.name, label: startLabel, placeholder: startProps.placeholder, value: value, onChange: onChange, "data-stid": "input-date", icon: "today", invalid: startProps.invalid, children: isMounted && (jsx(EGDSFauxInput, { "aria-label": buttonLabel, className: getClassNameList('uitk-form-field-trigger'), domRef: startProps.domRef, "data-name": startProps.name, id: `${startProps.id}-btn`, "data-stid": "open-date-picker", type: "button", onClick: setContext, analytics: startProps.analytics, children: formattedBtnText })) }) }), showDatePicker && triggeredField === 'start' && (jsx(EGDSMenuContainer, { position: PositionType.LEFT, className: containerClasses, autoPosition: true, children: jsx(DatePickerFooterAdditionalTextMenuView, { ...enhancedDatePickerProps }) }))] }) }) }), !singleMode && !useSingleTrigger && (jsx(EGDSLayoutFlexItem, { className: fieldWrapperClasses, grow: 1, minWidth: "zero", maxWidth: stacked ? undefined : 'half_width', children: jsx("div", { children: jsx(MenuEndDateField, { ...props }) }) }))] }) }));
};
MenuDateFields.displayName = 'MenuDateFields';
class DateField extends React.Component {
    static displayName = 'UitkDateField';
    static defaultProps = {
        dateFormat: EGDSDatePickerDateFormat.YMD,
        space: 'three',
        triggeredField: 'start',
    };
    render() {
        const { useCustomTrigger, datePickerProps } = this.props;
        if (useCustomTrigger) {
            switch (datePickerProps.renderType) {
                case EGDSDatePickerRenderType.DIALOG:
                    return jsx(CustomTriggerDialog, { ...this.props });
                case EGDSDatePickerRenderType.MENU:
                    return jsx(CustomTriggerMenu, { ...this.props });
                default:
                    return (jsxs(Viewport, { children: [jsx(ViewSmall, { children: jsx(CustomTriggerDialog, { ...this.props }) }), jsx(ViewLarge, { children: jsx(CustomTriggerMenu, { ...this.props }) })] }));
            }
        }
        switch (datePickerProps.renderType) {
            case EGDSDatePickerRenderType.DIALOG:
                return jsx(DialogDateFields, { ...this.props });
            case EGDSDatePickerRenderType.MENU:
                return jsx(MenuDateFields, { ...this.props });
            default:
                return (jsxs(Viewport, { children: [jsx(ViewSmall, { children: jsx(DialogDateFields, { ...this.props }) }), jsx(ViewLarge, { children: jsx(MenuDateFields, { ...this.props }) })] }));
        }
    }
}
const EGDSDateField = (props) => {
    const l10n = useLocalization();
    return jsx(DateField, { l10n: l10n, ...props });
};
EGDSDateField.displayName = 'EGDSDateField';
const EGDSNewDateField = EGDSDateField;

/* eslint-disable no-param-reassign */
const DAYS_IN_MS = 24 * 60 * 60 * 1000;
class DatePickerModel {
    isSelectingStart = false;
    isSelectingEnd = false;
    start;
    end;
    lastValidEndDate;
    singleDateSelect = false;
    // @ts-ignore
    holidays;
    // @ts-ignore
    holidaysName;
    // @ts-ignore
    holidaysConfigured;
    dayHolidays;
    isNotFirstTimeSameLength;
    constructor(endDate, singleDateSelect) {
        this.lastValidEndDate = endDate;
        this.singleDateSelect = singleDateSelect;
        this.dayHolidays = {};
        this.isNotFirstTimeSameLength = false;
    }
    get numberOfNights() {
        if (this.start && this.end) {
            return Math.round(Math.abs((this.end.getTime() - this.start.getTime()) / DAYS_IN_MS));
        }
        return 0;
    }
    // Helper method to valid if a given date is between start and end
    isDateSelected(date) {
        if (!this.start || !this.end) {
            return false;
        }
        this.setHolidaysSelection();
        return date.getTime() >= this.start.getTime() && date.getTime() <= this.end.getTime();
    }
    /*
     * Use this method to set dates for the model.
     * Setting start and end directly will result in bad logic
     * Notes:
     *  You may be tempted to simplify the logic below. Don't. This is easier to reason. Thanks :)
     */
    dateSelected(date) {
        // Nothing is selected
        if (!this.start && !this.end) {
            if (this.isSelectingStart) {
                this.start = date;
                this.setCurrentTrigger('end');
            }
            else if (this.isSelectingEnd) {
                this.end = date;
                this.setCurrentTrigger('start');
            }
            else {
                this.resetAndSetStart(date);
            }
        }
        else if (this.start && this.end) {
            // Both dates are selected
            if (this.isSelectingStart) {
                // Keep the same length of stay when reselecting start date
                this.selectStartWithSameLengthOfStay(date);
            }
            else if (this.isSelectingEnd) {
                this.selectEndWithAvailableStart(date);
            }
            else {
                this.resetAndSetStart(date);
            }
        }
        else if (this.start && !this.end) {
            // Only Start date is selected
            if (this.isSelectingStart) {
                this.start = date;
                this.setCurrentTrigger('end');
            }
            else if (this.isSelectingEnd) {
                this.selectEndWithAvailableStart(date);
            }
            else {
                this.resetAndSetStart(date);
            }
        }
        else {
            // Only End date is selected
            // eslint-disable-next-line no-lonely-if
            if (this.isSelectingStart) {
                this.selectStartWithAvailableEnd(date);
            }
            else if (this.isSelectingEnd) {
                this.end = date;
                this.setCurrentTrigger('start');
            }
            else {
                this.resetAndSetStart(date);
            }
        }
        this.setHolidaysSelection();
    }
    /*
     * Use this method to set single date for the model.
     */
    singleDateSelected(date) {
        this.start = date;
        this.end = undefined;
        this.setCurrentTrigger('start');
        this.setHolidaysSelection();
    }
    // Use this method to switch current trigger (TopBar)
    setCurrentTrigger(trigger) {
        if (trigger === 'start') {
            this.isSelectingStart = true;
            this.isSelectingEnd = false;
        }
        else if (trigger === 'end') {
            this.isSelectingStart = false;
            this.isSelectingEnd = true;
        }
        else {
            this.isSelectingStart = false;
            this.isSelectingEnd = false;
        }
    }
    // Private helper functions for dateSelected(date)
    selectEndWithAvailableStart(date) {
        if (this.start &&
            (date.getTime() < this.start.getTime() || (date.getTime() === this.start.getTime() && !this.singleDateSelect))) {
            this.resetAndSetStart(date);
        }
        else {
            this.end = date;
            this.setCurrentTrigger('start');
        }
    }
    selectStartWithAvailableEnd(date) {
        if (this.end && date.getTime() >= this.end.getTime()) {
            this.resetAndSetStart(date);
        }
        else {
            this.start = date;
            this.setCurrentTrigger('end');
        }
    }
    selectStartWithSameLengthOfStay(date) {
        const sameLengthOfStayEndDate = new Date(date.getTime() + this.numberOfNights * DAYS_IN_MS); // End must be updated before start
        if ((this.lastValidEndDate && this.lastValidEndDate <= sameLengthOfStayEndDate.getTime()) ||
            this.isNotFirstTimeSameLength) {
            this.resetAndSetStart(date);
        }
        else {
            this.isNotFirstTimeSameLength = true;
            this.end = sameLengthOfStayEndDate;
            this.start = date;
            this.setCurrentTrigger('end');
        }
    }
    resetAndSetStart(date) {
        this.start = date;
        this.end = undefined;
        this.setCurrentTrigger('end');
    }
    // Helper method to validate if the dates of holiday and calendar are the same (Format of holiday: yyyy-mm-dd)
    isDateHolidayEqual(dateHoliday, dateCalendar) {
        return this.holidaysConfigured && dateHoliday.toDateString() === dateCalendar.toDateString();
    }
    // Helper method to validate if the month (in the same year) of holiday and calendar are the same (Format of holiday: yyyy-mm-dd)
    // Format of monthInfoCalendar ({month, name, year})
    isMonthHolidayEqual(holiday, monthInfoCalendar) {
        if (this.holidaysConfigured) {
            return holiday.getMonth() === monthInfoCalendar.month && holiday.getFullYear() === monthInfoCalendar.year;
        }
        return false;
    }
    // Configure the holidays according to origin and destination
    configureHolidays(holidays) {
        this.dayHolidays = {};
        const holidaysOrigin = holidays.filter((holiday) => holiday.origin);
        const holidaysDestination = JSON.parse(JSON.stringify(holidays.filter((holiday) => !holiday.origin)));
        const holidaysDestinationFiltered = holidaysDestination.filter((destination) => !holidaysOrigin.find((origin) => origin.date === destination.date && origin.name === destination.name));
        holidaysDestinationFiltered.forEach((hd) => {
            hd.name += ` (${hd.country})`;
        });
        this.holidays = holidaysOrigin.concat(holidaysDestinationFiltered);
        this.holidays.forEach((hd) => {
            hd.dateSort = new Date(hd.date.split('-').join('/'));
            hd.dateSort.setHours(0, 0, 0, 0);
            hd.month = hd.dateSort.toLocaleString('default', {
                month: 'short',
            });
            // @ts-ignore
            this.dayHolidays[hd.dateSort.toDateString()] = true;
        });
        this.holidays.sort((date1, date2) => date1.dateSort &&
            date2.dateSort &&
            (date1.dateSort.getTime() > date2.dateSort.getTime() ||
                (date1.dateSort.getTime() === date2.dateSort.getTime() && date1.origin < date2.origin))
            ? 1
            : -1);
        this.holidaysConfigured = true;
        this.setHolidaysSelection();
    }
    // Helper method to return the holiday names in the dates selected
    setHolidaysSelection() {
        if (this.holidaysConfigured && (this.start || this.end)) {
            const start = new Date(this.start);
            start.setHours(0, 0, 0, 0);
            let end = new Date();
            if (this.end) {
                end = new Date(this.end);
                end.setHours(0, 0, 0, 0);
            }
            this.holidaysName = '';
            for (const holiday of this.holidays) {
                if ((this.end && holiday.dateSort && holiday.dateSort >= start && holiday.dateSort <= end) ||
                    (!this.end && holiday.dateSort && holiday.dateSort.getTime() === start.getTime())) {
                    this.holidaysName =
                        this.holidaysName === ''
                            ? `${holiday.month} ${holiday.dateSort.getDate()}: ${holiday.name}`
                            : `${this.holidaysName}  •  ${holiday.month} ${holiday.dateSort.getDate()}: ${holiday.name}`;
                }
            }
        }
        else {
            this.holidaysName = '';
        }
    }
}

export { DatePicker, DatePickerFooterAdditionalText, DatePickerFooterAdditionalTextDialogView, DatePickerFooterAdditionalTextMenuView, DatePickerModel, EGDSDateField, EGDSDatePickerDateFormat, EGDSDatePickerRenderType, EGDSDatePickerTriggeredSelector, EGDSNewDateField, EGDSNewDatePickerDialogView, EGDSNewDatePickerMenuView, EGDSDateField as UitkDateField, EGDSDatePickerDateFormat as UitkDatePickerDateFormat, EGDSDatePickerRenderType as UitkDatePickerRenderType, EGDSDatePickerTriggeredSelector as UitkDatePickerTriggeredSelector, EGDSNewDateField as UitkNewDateField, EGDSNewDatePickerDialogView as UitkNewDatePickerDialogView, EGDSNewDatePickerMenuView as UitkNewDatePickerMenuView };
//# sourceMappingURL=index.esm.js.map
